import { useNavigate } from 'react-router-dom'
import { useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGet, useTypeLoad } from 'utils/hooks'
import * as T from 'api/constants'
import { formatNumber } from 'utils/helpers'
import { clone, fromPairs, find, map, sortBy, cloneDeep, findIndex, filter, sumBy } from 'lodash'
import {
  clearFromState,
} from 'slices/globalSlice'
import {
  setTravelQuestionnaire,
  clearTravelQuestionnaire,
} from 'slices/travelSlice'
import {
  setPolicy,
  clearPolicy,
} from 'slices/tripProtectionSlice'
import {
  setKYCOptions,
  clearKYCOptions,
  setNewKYCOptions,
  clearNewKYCOptions,
} from 'slices/kyxSlice'
import {
  setGeneralOptions,
  clearGeneralOptions,
  openModal,
  closeModal,
  openDrawer,
  closeDrawer,
  setDrawerUtils,
  openAlert,
  closeAlert,
  openChat,
  closeChat,
  openPopover,
  removePopover,
  closePopover,
  openSidenav,
  closeSidenav,
  toggleAutoInput,
  openAutomationLegend,
  closeAutomationLegend,
  openToast,
} from 'slices/appSlice'

export const useAction = () => {
  const dispatch = useDispatch()

  return useMemo(() => ({
    // get: (type, data) => dispatch({ type, ...data }),
    getUser: () => dispatch({ type: T.GET_USER }),
    getCompany: () => dispatch({ type: T.GET_COMPANY }),
    clearFromState: (data = []) => dispatch(clearFromState(data)),
    getSavingsTotals: () => dispatch({ type: T.GET_SAVINGS_TOTALS }),
    closeModal: () => dispatch(closeModal()),
    openModal: (view, modalProps) => dispatch(openModal({ view, modalProps })),
    closeDrawer: () => dispatch(closeDrawer()),
    openDrawer: (drawerView, drawerProps) => dispatch(openDrawer({ drawerView, drawerProps })),
    setDrawerUtils: drawerUtils => dispatch(setDrawerUtils(drawerUtils)),
    closeAlert: () => dispatch(closeAlert()),
    openAlert: (alertView, alertProps) => dispatch(openAlert({ alertView, alertProps })),
    openToast: (view, toastProps) => dispatch(openToast({ view, toastProps })),
    openChat: () => dispatch(openChat()),
    closeChat: () => dispatch(closeChat()),
    openPopover: (renderPopover, popoverTarget) => dispatch(openPopover({ renderPopover, popoverTarget })),
    removePopover: () => dispatch(removePopover()),
    closePopover: () => dispatch(closePopover()),
    openSidenav: () => dispatch(openSidenav()),
    closeSidenav: () => dispatch(closeSidenav()),
    setGeneralOptions: data => dispatch(setGeneralOptions(data)),
    clearGeneralOptions: () => dispatch(clearGeneralOptions()),
    openAutomationLegend: () => dispatch(openAutomationLegend()),
    closeAutomationLegend: () => dispatch(closeAutomationLegend()),
    toggleAutoInput: () => dispatch(toggleAutoInput()),
    openTravelQuestionnaire: startingPage => dispatch(setGeneralOptions({ travelQuestionnaireActive: true, travelQuestionnaireStartingPage: startingPage || 'DESTINATION_CONFIDENCE' })),
    closeTravelQuestionnaire: () => dispatch(setGeneralOptions({ travelQuestionnaireActive: false, travelQuestionnaireStartingPage: 'DESTINATION_CONFIDENCE' })),
    setTravelQuestionnaire: data => dispatch(setTravelQuestionnaire(data)),
    clearTravelQuestionnaire: () => dispatch(clearTravelQuestionnaire()),
  }), [])
}
export const useExternalAccounts = () => useGet(T.GET_EXTERNAL_ACCOUNTS, 'externalAccounts', null)
export const useRandomDestinations = () => useGet(T.GET_RANDOM_DESTINATIONS, 'randomDestinations', [])
export const useUserSettings = () => useGet(T.GET_USER_SETTINGS, 'userSettings', null)
export const useCompanySettings = () => useGet(T.GET_COMPANY_SETTINGS, 'companySettings', null)
export const useSavingsTotals = () => {
  const user = useSelector(({ global }) => global.user)
  const totals = useGet(T.GET_SAVINGS_TOTALS, 'savingsTotals')
  const company = useSelector(({ global }) => global.company)
  const contributionSettings = useSelector(({ global }) => global.contributionSettings)

  return useMemo(() => {
    const temp = {
      ...totals,
      currentSavings: +totals.bank_actual_total || 0,
      currentCompanyPoints: +totals.company_credits_total || 0,
      currentPoints: +totals.donde_credits_total || 0,
      hasMatchGroups: !!company?.match_settings?.length,
      hasWallet: user.has_savings_account,
      isWalletCapable: user.is_wallet_capable,
      hasAccount: !!user.primary_connected_account_id || contributionSettings?.user_settings?.has_payroll,
      hasCompanyContribution: !!+totals.company_contribution_total,
      hasEmployeeContribution: !!+totals.employee_contribution_total,
      needsContribution: !+contributionSettings?.user_settings?.amount && !+contributionSettings?.user_settings?.payroll_percentage_amount && !+contributionSettings?.user_settings?.payroll_fixed_amount,
      pointsExpiring: sumBy(totals.expiring_credits, o => +o.remaining_amount),
      isDondeIsland: user.is_donde_island,
      isClubDonde: user.is_club_donde,
      isCustomerInstance: user.is_donde_island || user.is_club_donde,
      isRewardEligible: user.vendor === 1,
      formatCurrency: (amount, options = { fixedDecimal: true }) => formatNumber(+amount, options.fixedDecimal),
      formatPoints: (amount, options = { fixedDecimal: true, full: false }) => `${formatNumber(+amount, options.fixedDecimal)} ${options.full ? 'Dónde Points' : 'pts'}`,
    }

    temp.programRewards = Math.max((+totals.company_contribution_total || 0) - +totals.employee_spend_total, 0) + temp.currentCompanyPoints
    temp.hasPoints = !!temp.currentPoints
    temp.hasCompanyPoints = !!temp.currentCompanyPoints
    temp.totalPoints = temp.currentPoints + temp.currentCompanyPoints
    temp.hasAnyPoints = !!+temp.totalPoints
    temp.hasPointsExpiring = !!+temp.pointsExpiring
    temp.needsWallet = !temp.hasWallet && temp.isWalletCapable
    temp.needsAccount = temp.hasWallet && !temp.hasAccount
    temp.needsSavings = temp.hasWallet && temp.hasAccount && temp.needsContribution
    temp.hasCurrentSavings = !!temp.currentSavings
    temp.spendingPower = temp.currentSavings + temp.currentCompanyPoints + temp.currentPoints
    temp.hasSpendingPower = !!temp.spendingPower
    temp.nextStep = temp.needsWallet ? 'wallet' : temp.needsAccount ? 'account' : temp.needsSavings ? 'savings' : null

    return temp
  }, [totals, user, contributionSettings, company])
}
export const useMyTrips = () => useGet(T.GET_MY_TRIPS, 'trip_data', null)

export const usePolicy = () => {
  const dispatch = useDispatch()
  const policy = useSelector(({ tripProtection }) => tripProtection.policy)
  const insuranceUpfrontQuote = useSelector(({ global }) => global.insuranceUpfrontQuote) || {}
  const insuranceFinalQuote = useSelector(({ global }) => global.insuranceFinalQuote) || {}
  const insuranceProducts = useGet(T.GET_INSURANCE_PRODUCTS, 'insuranceProducts', [])
  const selectedProduct = useMemo(() => {
    return find(insuranceProducts, { vendor_id: policy.vendor_id })
  }, [insuranceProducts, policy.vendor_id])
  const sortedBenefits = useMemo(() => {
    return sortBy(map(selectedProduct?.product_data?.benefits, (o, key) => ({ 
      ...o, 
      key, 
      amount: insuranceUpfrontQuote[`${key}_amount`],
    })), o => o.most_popular).filter(o => policy.state === 'FL' ? o.id !== 'benefit_17' : true)
  }, [selectedProduct, insuranceUpfrontQuote, policy.state])
  const benefitsObject = fromPairs(filter(policy.benefits.map(({ id, value }) => [id, value]), ([, value]) => value))

  const setPolicyUtil = data => dispatch(setPolicy(data))

  const getIsActive = id => policy.benefits[findIndex(policy.benefits, { id })]?.value

  return {
    policy,
    numberOfBenefits: sumBy(policy.benefits, o => o.value ? 1 : 0),
    benefitsObject,
    setPolicy: setPolicyUtil,
    clearPolicy: () => dispatch(clearPolicy()),
    addTraveler: () => {
      setPolicyUtil({ 
        travelers: [
          ...policy.travelers, 
          { 
            age: '', 
            dob: null, 
            name: '', 
            middle_name: '', 
            surname: '', 
            individual_trip_cost: '',
          }],
      })
    },
    setTraveler: (index, data) => {
      const temp = clone(policy.travelers)
      temp.splice(index, 1, { ...policy.travelers[index], ...data })
      setPolicyUtil({ travelers: temp })
    },
    removeTraveler: index => {
      const temp = clone(policy.travelers)
      temp.splice(index, 1)
      setPolicyUtil({ travelers: temp })
    },
    addDestinationCountry: value => setPolicyUtil({ destinations: [...policy.destinations, value] }),
    removeDestinationCountry: index => {
      const temp = clone(policy.destinations)
      temp.splice(index, 1)
      setPolicyUtil({ destinations: temp })
    },
    getIsActive,
    addBenefit: id => {
      const index = findIndex(policy.benefits, { id })
      const temp = cloneDeep(policy.benefits)
      temp[index].value = true
      setPolicyUtil({ benefits: temp })
    },
    removeBenefit: id => {
      const index = findIndex(policy.benefits, { id })
      const temp = cloneDeep(policy.benefits)
      temp[index].value = false
      setPolicyUtil({ benefits: temp })
    },
    toggleBenefit: id => {
      const temp = cloneDeep(policy.benefits)
      const index = findIndex(policy.benefits, { id })
      if (id === 'benefit_5' && getIsActive('benefit_5') && getIsActive('benefit_8')) {
        temp[findIndex(policy.benefits, { id: 'benefit_8' })].value = false
      } else if (id === 'benefit_8' && !getIsActive('benefit_8') && !getIsActive('benefit_5')) {
        temp[findIndex(policy.benefits, { id: 'benefit_5' })].value = true
      }
      temp[index].value = !temp[index].value
      setPolicyUtil({ benefits: temp })
    },
    getUpfrontQuote: () => {
      dispatch({ 
        type: T.GET_INSURANCE_UPFRONT_QUOTE, 
        productID: policy.product_id, 
        data: {
          bf: {
            country_state: policy.state, // 'UT'
            age: +policy.travelers[0]?.age, // '30'
            deposit_date: policy.start_date, // '2023-06-01'
            start_date: policy.start_date, // '2023-06-01'
            end_date: policy.end_date, // '2023-06-01'
            destination_country_ids: policy.destinations.map(o => o.iso_code).join(','), // DE,JP
            host_country_id: 'US',
            main_applicant_trip_cost: +policy.travelers[0]?.individual_trip_cost, // 500
            ...benefitsObject,

            // additional_travelers: policy.travelers.map((o, idx) => !!idx && ({ age: +o.age, trip_cost: +o.individual_trip_cost })).filter(o => !!o),
            // product_id: +policy.vendor_id, // 12
            // currency_id: 'USD',
            // number_of_reservations: 1,
            // number_of_reservations: policy.number_of_reservations,
            // deposit_date: '',
          },
          donde: {
            name: policy.destinations.map(o => o.name).join(', '),
          },
        },
      })
    },
    getCustomPackageQuote: () => {
      dispatch({ type: T.GET_INSURANCE_FINAL_QUOTE, productID: policy.vendor_id, data: policy })
    },

    getQuote: all => {
      const commonData = {
        country_state: policy.state, // 'UT'
        start_date: policy.start_date, // '2023-06-01'
        end_date: policy.end_date, // '2023-06-01'
        additional_travelers: policy.travelers.map((o, idx) => !!idx && ({ age: +o.age, trip_cost: +o.individual_trip_cost })).filter(o => !!o),
        destination_country_ids: policy.destinations.map(o => o.iso_code).join(','),
      }
      if (all) {
        const singleTripData = {
          trip_cost: +policy.travelers[0]?.individual_trip_cost,
          // main_applicant_trip_cost: +policy.travelers[0]?.individual_trip_cost,
          age: +policy.travelers[0]?.age, // '30'
          benefit_1: true,
          benefit_3: true,
          benefit_5: true,
          // benefit_6: policy.benefit_6,
          benefit_8: policy.state !== 'NY',
          benefit_10: policy.state !== 'FL',
          benefit_12: policy.benefit_12,
          benefit_15: true,
          benefit_16: true,
          benefit_17: policy.state !== 'FL',
          benefit_18: policy.benefit_18,
          number_of_reservations: policy.number_of_reservations,
          ...commonData,
        }
        if (['CA', 'NH'].includes(policy.state)) delete singleTripData.benefit_12
        const multiTripData = {
          product_id: +policy.vendor_id,
          dobs: policy.travelers.map(o => o.dob),
          trip_cost: policy.coverage_limit,
          country_state: policy.state, // 'UT'
          start_date: policy.start_date, // '2023-06-01'
        }
        dispatch({ 
          type: T.GET_INSURANCE_UPFRONT_QUOTE, 
          productID: policy.product_id, 
          data: {
            bf: selectedProduct.is_single_trip ? singleTripData : multiTripData,
            donde: {
              name: policy.destinations.map(o => o.name).join(', '),
            },

            // trip_cost: policy.travelers[0]?.individual_trip_cost,
            // deposit_date: policy.start_date, // '2023-06-01'
            // deposit_date: '2023-05-01',
            // trip_cost: 1000,
            // trip_cost: policy.travelers.reduce((total, o) => total + +o.individual_trip_cost, 0),
          },
        })

      } else {
        const singleTripData = {
          number_of_reservations: benefitsObject.benefit_18 ? policy.number_of_reservations : undefined,
          product_id: +policy.vendor_id,
          currency_id: 'USD',
          host_country_id: 'US',
          main_applicant_trip_cost: policy.travelers[0]?.individual_trip_cost,
          age: policy.travelers[0]?.age, // '30'
          deposit_date: policy.deposit_date,
          // trip_cost: policy.travelers[0]?.individual_trip_cost,
          
          // deposit_date: policy.start_date, // '2023-06-01'
          // deposit_date: '2023-05-01',
          
          // trip_cost: 1000,
          // trip_cost: policy.travelers.reduce((total, o) => total + +o.individual_trip_cost, 0),
          ...{
            ...benefitsObject,
            // benefit_6: benefitsObject.benefit_6 && policy.benefit_6,
            benefit_5: !!benefitsObject.benefit_5,
            benefit_12: benefitsObject.benefit_12 && policy.benefit_12,
            benefit_18: benefitsObject.benefit_18 && policy.benefit_18,
          },
          ...commonData,
        }
        const multiTripData = {
          product_id: +policy.vendor_id,
          dobs: policy.travelers.map(o => o.dob),
          trip_cost: policy.coverage_limit,
          country_state: policy.state, // 'UT'
          start_date: policy.start_date, // '2023-06-01'
        }
        dispatch({ 
          type: T.GET_INSURANCE_FINAL_QUOTE, 
          productID: policy.product_id, 
          data: {
            bf: selectedProduct.is_single_trip ? singleTripData : multiTripData,
            donde: {
              trip_id: policy.trip_id,
              name: policy.destinations.map(o => o.name).join(', '),
            },
          },
        })
      }
    },
    insuranceProducts,
    insuranceUpfrontQuote,
    insuranceFinalQuote,
    selectedProduct,
    sortedBenefits,
    estimatedCost: +insuranceUpfrontQuote.total || policy.benefits.reduce((total, o) => total + (o.value ? insuranceUpfrontQuote[`${o.id}_amount`] : 0), 0),
    premium: +insuranceFinalQuote?.final_price || 0,
    quoteID: insuranceFinalQuote?.quote_id,
    // estimatedCost: policy.benefits.reduce((total, o) => total + +o.individual_trip_cost, 0),
  }
}

export const useKYC = () => {
  const dispatch = useDispatch()
  const kycOptions = useSelector(({ kyx }) => kyx.kycOptions)

  return useMemo(() => ({
    setKYCOption: data => dispatch(setKYCOptions(data)),
    clearKYCOptions: () => dispatch(clearKYCOptions()),
    kycOptions,
  }), [kycOptions])
}

// const DWOLLA_STATUS = {
//   DEFAULT: 0,
//   UNVERIFIED: 1,
//   VERIFIED: 2,
//   RETRY: 3,
//   DOCUMENT: 4,
//   SUSPENDED: 5,
//   DEACTIVATED: 6,
//   REACTIVATED: 7,
//   KBA: 8,
//   SUBMITTED: 9,
// }

const NEXT_PAGE = {
  2: '/onboarding/savings-account-ready',
  4: '/onboarding/documents-required',
  'kyc.verification.document_required': '/onboarding/documents-required',
  // 'kyc.verification.under_review': '/onboarding/',
  5: '/onboarding/account-suspended',
  // 9: '/onboarding/documents-required',
}

export const useNewKYC = ({
  isWelcomeRedirect,
  nextPage,
  onSuccess,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(({ global }) => global.user)
  const kycOptions = useSelector(({ kyx }) => kyx.kycOptions_new)
  const onboardingDocuments = useSelector(({ global }) => global.onboardingDocuments)

  const onNext = override => {
    const statusPage = NEXT_PAGE[user.savings_account_status] || NEXT_PAGE[user.savings_account_sub_status]
    if (isWelcomeRedirect ? !!statusPage : true) {
      navigate(override || nextPage || statusPage || '/')
    }
  }

  const submitKYC = () => {
    dispatch({ 
      type: T.SET_ONBOARDING_GENERAL_INFO, 
      data: kycOptions,
    })
  }

  const getDocuments = () => dispatch({ type: T.GET_ONBOARDING_DOCUMENTS })

  const getStatus = () => dispatch({ type: T.GET_ONBOARDING_STATUS })

  const { isLoading } = useTypeLoad(T.SET_ONBOARDING_GENERAL_INFO, data => {
    if (!data.error) {
      onSuccess?.()
      onNext(user.savings_account_status === 9 ? '/onboarding/information-being-reviewed-create-account' : undefined)
    }
  })

  // useEffect(() => {
  //   if (user.savings_account_status === 4 || user.savings_account_sub_status === 'kyc.verification.document_required') {
  //     getDocuments()
  //   }
  // }, [user.status])

  return {
    onNext,
    setKYCOption: data => dispatch(setNewKYCOptions(data)),
    clearKYCOptions: () => dispatch(clearNewKYCOptions()),
    submitKYC,
    getDocuments,
    getStatus,
    kycOptions,
    isLoading,
    onboardingDocuments,
  }
}

const ACTIONS = {
  default: T.REQUEST_GENERAL_VC,
  stay: T.REQUEST_PARTNER_VC,
  air: T.REQUEST_GENERAL_VC,
  affiliate: T.REQUEST_GENERAL_VC,
  viator: T.REQUEST_VIATOR_VC,
  general: T.REQUEST_GENERAL_VC,
  unrestricted: T.REQUEST_GENERAL_VC,
}

export const useVCC = ({
  type = 'default', // default | air | stay | affiliate | general | unrestricted
  vendor,
  partner_uid,
  travel_content_uid,
  cb,
  paymentValues,
}) => {
  const dispatch = useDispatch()
  const ACTION = ACTIONS[type] || ACTIONS.default
  const user = useSelector(({ global }) => global.user)
  const [values, setValues] = useState({
    full_name: `${user.first_name} ${user.last_name}`,
    confirmed_email: '',
    vendor,
    purchase_details: '',
    start_date: null,
    end_date: null,

    supplier_name: '',
    purchase_type: '',
  })

  const isVCCValid = useMemo(() => {
    return !!values.full_name?.length && 
      values.confirmed_email?.toLowerCase() === user.email?.toLowerCase() &&
      !!values.vendor.length && 
      !!values.purchase_details.length &&
      (type === 'stay' ? !!values.start_date && !!values.end_date && values.start_date < values.end_date : true) &&
      paymentValues.isPaymentValid
  }, [
    values, 
    paymentValues,
    user.email, 
  ])

  useTypeLoad(ACTION, data => {
    if (!data.error) cb?.()
  })

  const onRequestVCC = () => {
    const data = {
      full_name: values.full_name,
      confirmed_email: values.confirmed_email,
      purchase_details: values.purchase_details,
      card_amount: paymentValues?.cardAmount,
      donde_amount: paymentValues?.dondeAmount,
      credit_amount: paymentValues?.dondePointsAmount,
      company_credit_amount: paymentValues?.companyPointsAmount,
      transaction_fee: paymentValues?.transactionFee,
      amount: paymentValues?.dondeAmount,
      ...(paymentValues?.ccData || {}),
      card_address1: paymentValues?.address,
      card_city: paymentValues?.city,
      card_state: paymentValues?.stateProvince,
      card_postal: paymentValues?.postalCode,
      card_country_code: paymentValues?.countryCode,
    }
    dispatch({
      type: ACTION,
      data: ['general', 'air', 'affiliate', 'unrestricted'].includes(type) ? {
        ...data,
        travel_content_uid,
        vendor: values.vendor,
        supplier_name: values.vendor,
        purchase_type: values.purchase_type === 'affiliate' ? 'other' : (values.purchase_type || 'other'),
        start_date: values.start_date?.format?.('YYYY-MM-DD'),
        end_date: values.end_date?.format?.('YYYY-MM-DD'),
      } : type === 'viator' ? {
        ...data,
      } : {
        ...data,
        partner_uid,
        travel_content_uid,
        supplier_name: values.vendor,
        start_date: values.start_date?.format?.('YYYY-MM-DD'),
        end_date: values.end_date?.format?.('YYYY-MM-DD'),
      },
    })
  }

  const onChange = useCallback(data => {
    setValues(prev => ({
      ...prev,
      ...data,
    }))
  }, [])

  return useMemo(() => { 

    return {
      onRequestVCC,
      onChange,
      isVCCValid,
      values,
      emailError: values.confirmed_email.length > 0 && values.confirmed_email?.toLowerCase() !== user.email?.toLowerCase(),
    }
  }, [
    isVCCValid, 
    paymentValues,
    values, 
    user.email,
  ])
}